import { useMemo, useState } from "react";
import debounce from "lodash.debounce";
import { useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { GenericTable } from "../../../Shared/components/Table/GenericTable";
import { TableActionButtons } from "../../../Shared/components/Table/TableActionButtons";
import { PageOptionsDto } from "../../../Shared/dto/page.options.dto";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { IVehicle } from "../models/Vehicle";
import { VehicleService } from "../services/VehicleService";
import { VehicleForm } from "./VehicleForm";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { AuthService } from "../../Auth/services/AuthService";
import { useNavigate } from "react-router-dom";
import { FileImportForm } from "../../../Shared/components/FileImportForm";
import { useTranslation } from "react-i18next";
import {
  ImportFileActionType,
  ImportFileLabelType,
} from "../../../Shared/enums/importFileActionType";
import { ConfirmDialog } from "../../../Shared/components/ConfirmDialog";

export const Vehicles = () => {
  const navigate = useNavigate();
  const {t} = useTranslation()
  const message = t('loading_message');
  const isAllowed = AuthService.isManagementAllowed();

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [selectedVehicles, setSelectedVehicles] = useState<IVehicle[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<IVehicle | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const companies = AuthService.getLocalUser()?.user?.companies;

  const findVehicles = async () => {
    try {
      const pageOptions: PageOptionsDto = {
        page: pagination.pageIndex + 1,
        take: pagination.pageSize,
        query: globalFilter,
      };
      setIsLoading(true);
      const response = await VehicleService.getAll(pageOptions);
      setIsLoading(false);
      return response;
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const query = useQuery({
    queryKey: [
      "vehicles",
      pagination.pageIndex,
      pagination.pageSize,
      globalFilter,
    ],
    queryFn: findVehicles,
    placeholderData: (previous) => previous?.data,
  });

  const debouncedSetFilter = useMemo(
    () =>
      debounce((val: string) => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        setGlobalFilter(val);
      }, 200),
    []
  );

  const onPaginationChange = (pageIndex: number, pageSize: number) => {
    setPagination({ pageIndex, pageSize });
  };

  const getPageCount = () => {
    const total = query.data?.meta?.itemCount ?? 0;
    return Math.ceil(total / pagination.pageSize);
  };

  const onRowEditAction = (row: IVehicle) => {
    setSelectedVehicle(row);
    onAddAction();
  };
  const onRowViewAction = (row: IVehicle) => {
    setSelectedVehicle(row);
    navigate(`/services/vehicles/details/?vehicle=${row.id}`);
  };
  const onRowDeleteAction = (row: IVehicle) => {
    setSelectedVehicle(row);
    setIsDialogOpen(true);
  };

  const deleteAction = async () => {
    const approvals =
      selectedVehicles?.length > 0
        ? selectedVehicles.map((x) => x.id!)
        : [selectedVehicle?.id ?? ""];
    try {
      const response = await VehicleService.delete(approvals);
      if (response) {
        query.refetch();
      }
    } catch (error: any) {
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    }
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const onAddAction = () => setIsModalOpen(!isModalOpen);
  const toggleModal = (success: boolean) => {
    setIsModalOpen(!isModalOpen);
    if (success) {
      query.refetch();
    }
  };

  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const onImportAction = () => setIsImportModalOpen(!isModalOpen);
  const toggleImportModal = (success: boolean) => {
    setIsImportModalOpen(!isImportModalOpen);
    if (success) {
      query.refetch();
    }
  };

  const onExportAction = () => {
    UtilService.exportToExcel(query?.data?.data, t('truck'));
  };

  const tableAction = !isAllowed ? (
    <TableActionButtons
      onAdd={onAddAction}
      onImport={onImportAction}
      onExport={onExportAction}
    />
  ) : (
    <TableActionButtons onExport={onExportAction} />
  );

  const columns = useMemo<ColumnDef<IVehicle>[]>(
    () => [
      {
        accessorKey: "name",
        header: t('truck_make_label'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "chassisNumber",
        header: t('truck_chassis_label'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "plateNumber",
        header: t('truck_registration_label'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "vehicleType",
        header: t('truck_type_label'),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "createdAt",
        header: t('date_created'),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    []
  );

  return (
    <div className="">
      <div className="relative">
        {isModalOpen && (
          <VehicleForm
            onClose={toggleModal}
            vehicle={selectedVehicle ?? undefined}
            companies={companies!}
          />
        )}

        {isImportModalOpen && (
          <FileImportForm
            onClose={toggleImportModal}
            actionType={ImportFileActionType.VEHICLES}
            actionLabel={ImportFileLabelType.VEHICLES}
          />
        )}

        <ConfirmDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onConfirm={deleteAction}
          itemName={selectedVehicle?.name}
        />
        <GenericTable
          isLoading={isLoading}
          data={query?.data?.data ?? []}
          meta={query?.data?.meta ?? 0}
          columns={columns}
          globalFilter={globalFilter}
          onGlobalFilterChange={debouncedSetFilter}
          pageCount={getPageCount()}
          onPaginationChange={onPaginationChange}
          onRowSelectionChange={setSelectedVehicles}
          {...(!isAllowed && { onEdit: onRowEditAction })}
          onMore={onRowViewAction}
          onDelete={onRowDeleteAction}
          actionButtons={tableAction}
        />
      </div>
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
      {isLoading && <OverlayLoader message={message} />}
    </div>
  );
};
